import React, { useState, useContext, useEffect, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { Vehicle } from 'gw-capability-quoteandbind';
import { MLRiskAnalysisService } from 'ml-services-common-react';
import { platformMessages } from 'ml-components-platform-react';
import { DateValue } from '@jutro/components';
import metadata from './MLRatingFactorPrefillComponent.metadata.json5';
import messages from './MLRatingFactorPrefillComponent.messages.js';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';

import { IntlContext } from '@jutro/legacy/locale';

const MLRatingFactorPrefillComponent = (props) => {
    const { jobOrPolicyNumber, isQuote, isPolicy, isPersonalAuto, isPersonalUmbrella, styles, authHeader, viewModelService } = props;
    
    const translator = useTranslator();

    const GENDERTYPE_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('GenderType');
    const MARITALSTATUS_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('MaritalStatus');
    const DRIVERSTATUSTYPE_ML_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('DriverStatusType_ML');
    const REPORTTYPE_EXT_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('ReportType_Ext');

    const [prefill, setPrefill] = useState({});
    const intl = useContext(IntlContext);

    const setPrefillData = (prefillDtoML) => {
        if (isPersonalAuto) {
            setPrefill((prevState) => {
                return {
                    ...prevState,
                    drivers: prefillDtoML.lobs.personalAuto.drivers,
                    vehicles: _.map(prefillDtoML.lobs.personalAuto.vehicles, (vehicle) => {
                        return new Vehicle(vehicle);
                    }),
                    orderInfo: prefillDtoML.lobs.personalAuto.orderInfo
                }
            });
        } else if (isPersonalUmbrella) {
            setPrefill((prevState) => {
                return {
                    ...prevState,
                    drivers: prefillDtoML.lobs.personalUmbrella.drivers,
                    vehicles: _.map(prefillDtoML.lobs.personalUmbrella.vehicles, (vehicle) => {
                        return new Vehicle(vehicle);
                    }),
                    orderInfo: prefillDtoML.lobs.personalUmbrella.orderInfo
                }
            });
        }
    };
    
    const getAndSetPrefillBySubmissionNumber = () => {
        const prefillSubPromise = MLRiskAnalysisService.getRAPrefillDetailsMLByJobNumber(jobOrPolicyNumber, authHeader);
        prefillSubPromise.then((prefillDtoML) => {
            setPrefillData(prefillDtoML);
        }, () => {
            setPrefill({});
        });
    };

    const getAndSetPrefillByPolicyNumber = () => {
        const prefillPolPromise = MLRiskAnalysisService.getRAPrefillDetailsMLByPolicyNumber(jobOrPolicyNumber, authHeader);
        prefillPolPromise.then((prefillDtoML) => {
            setPrefillData(prefillDtoML);
        }, () => {
            setPrefill({});
        });
    };

    const getVehicleTitle = (vehicle) => {
        return vehicle.getYearMakeModelNameML();
    };

    const getMaskedLicenseNumber = (licenseNumber) => {
        return licenseNumber ?
            `${licenseNumber}${translator(messages.licenseNumberMask)}` : '';
    };

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    useEffect(() => {
        if (isQuote) {
            getAndSetPrefillBySubmissionNumber();
        } else if (isPolicy) {
            getAndSetPrefillByPolicyNumber();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getYesNo = (value) => {
        if (value === true) {
            return platformMessages.mlYes;
        } 
        return platformMessages.mlNo;
    }

    const getPrefillOrderInfoTableOverrides = () => {
        const getCellInsuredName = (rowData) => {
            return rowData.insuredName;
        };
    
        const getCellAddress = (rowData) => {
            return rowData.address;
        };
    
        const getCellOrderDate = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.orderDate} />;
        };
    
        const getCellReturnDate  = (rowData) => {
            return <DateValue displayFormat="vshort" value={rowData.returnDate} />;
        };
    
        const getCellVendorName = (rowData) => {
            return rowData.vendorName;
        };

        return {
            insuredNameColumn: {
                renderCell: getCellInsuredName
            },
            addressColumn: {
                renderCell: getCellAddress
            },
            orderDateColumn: {
                renderCell: getCellOrderDate
            },
            returnDateColumn: {
                renderCell: getCellReturnDate
            },
            vendorNameColumn: {
                renderCell: getCellVendorName
            }
        }
    }

    const getPrefillReturnInfoTableOverrides = () => {
        const getCellEmailAddress = (rowData) => {
            return rowData.emailAddress;
        };
    
        const getCellPhoneNumber = (rowData) => {
            return rowData.phoneNumber;
        };
    
        const getCellHomeOwnership = (rowData) => {
            return translator(getYesNo(rowData.homeOwnership));
        };
    
        const getCellTimeAtResidenceColumn  = (rowData) => {
            return rowData.timeAtResidence;
        };

        return {
            emailAddressColumn: {
                renderCell: getCellEmailAddress
            },
            phoneNumberColumn: {
                renderCell: getCellPhoneNumber
            },
            homeOwnershipColumn: {
                renderCell: getCellHomeOwnership
            },
            timeAtResidenceColumn: {
                renderCell: getCellTimeAtResidenceColumn
            }
        }
    }

    const getPrefillDriversTableOverrides = () => {
        const getTypeKeyDescription = (typeKeyName) => {
            return translator({
                id: typeKeyName,
                defaultMessage: typeKeyName
            });
        };

        const getCellDriver = (rowData) => {
            return <strong>{rowData.displayName}</strong>;
        };
    
        const getCellDateOfBirth = (rowData) => {
            return rowData.dateOfBirth?.year ?? '';
        };
    
        const getCellGender = (rowData) => {
            return rowData.gender ? getTypeKeyDescription(GENDERTYPE_TYPELIST.getCode(rowData.gender).name) : '';
        };
    
        const getCellMaritalStatus = (rowData) => {
            if (rowData.maritalStatus) {
                if (rowData.maritalStatus === 'U') {
                    return 'Unknown';
                }
                return getTypeKeyDescription(MARITALSTATUS_TYPELIST.getCode(rowData.maritalStatus).name);
            }
            return '';
        };
    
        const getCellLicenseNumber = (rowData) => {
            return getMaskedLicenseNumber(rowData.licenseNumber);
        };

        const getCellNonDriverReason = (rowData) => {
            return rowData.nonDriverReason ? getTypeKeyDescription(DRIVERSTATUSTYPE_ML_TYPELIST.getCode(rowData.nonDriverReason).name) : '';
        };

        const getCellReportType = (rowData) => {
            return rowData.reportType ? getTypeKeyDescription(REPORTTYPE_EXT_TYPELIST.getCode(rowData.reportType).name) : '';
        };

        const getCellReportDate = (rowData) => {
            return rowData.driverDiscoveryDate ? getFormattedDateString(rowData.driverDiscoveryDate) : '';
        };

        return {
            driverColumn: {
                renderCell: getCellDriver
            },
            dateOfBirthColumn: {
                renderCell: getCellDateOfBirth
            },
            genderColumn: {
                renderCell: getCellGender
            },
            maritalStatusColumn: {
                renderCell: getCellMaritalStatus
            },
            licenseNumberColumn: {
                renderCell: getCellLicenseNumber
            },
            nonDriverReasonColumn: {
                renderCell: getCellNonDriverReason
            },
            reportTypeColumn: {
                renderCell: getCellReportType
            },
            reportDateColumn: {
                renderCell: getCellReportDate
            }
        }
    }

    const getPrefillVehiclesOverrides = () => {
        const getCellVehicleDetailsHeader = (rowData) => {
            return <strong>{translator(rowData.header)}</strong>;
        };

        const getCellVehicleDetailsValue = (rowData) => {
            return rowData.value;
        };

        const formatVehicleData = (vehicle) => {
            let data = [
                {
                    header: messages.vin,
                    value: vehicle.vin
                },
                {
                    header: messages.licenseState,
                    value: vehicle.licenseState
                },
                {
                    header: messages.salvageTitle,
                    value: translator(getYesNo(vehicle.salvageTitle))
                },
                {
                    header: messages.vehicleType,
                    value: vehicle.vehicleType
                },
                {
                    header: messages.usedForBusiness,
                    value: translator(getYesNo(vehicle.usedForBusiness))
                },
            ];

            if (vehicle.lienholderName) {
                data = [...data, 
                    {
                        header: messages.lienholderName,
                        value: vehicle.lienholderName
                    }
                ]
            }

            if (vehicle.lienholderAddress) {
                data = [...data, 
                    {
                        header: messages.lienholderAddress,
                        value: vehicle.lienholderAddress
                    }
                ]
            }

            if (vehicle.secondLienholderName) {
                data = [...data, 
                    {
                        header: messages.secondLienholderName,
                        value: vehicle.secondLienholderName
                    }
                ]
            }

            if (vehicle.secondLienholderAddress) {
                data = [...data, 
                    {
                        header: messages.secondLienholderAddress,
                        value: vehicle.secondLienholderAddress
                    }
                ]
            }
            
            return data;
        }

        const overrides = _.get(prefill, 'vehicles', []).map((vehicle, index) => {
            return {
                [`prefillVehicleTable${index}`]: {
                    data: formatVehicleData(vehicle),
                    title: getVehicleTitle(vehicle)
                },
                [`vehicleDetailsHeaderColumn${index}`]: {
                    renderCell: getCellVehicleDetailsHeader
                },
                [`vehicleDetailsValueColumn${index}`]: {
                    renderCell: getCellVehicleDetailsValue
                }
            }
        });

        return Object.assign({}, ...overrides);
    }

    const overrideProps = {
        prefillOrderInfoTable: {
            title: messages.orderInformation,
            data: prefill.orderInfo ? [prefill.orderInfo] : []
        },
        prefillReturnInfoTable: {
            title: messages.returnInformation,
            data: prefill.orderInfo ? [prefill.orderInfo] : []
        },
        prefillDriversTable: {
            title: messages.drivers,
            data: prefill.drivers
        },
        prefillVehiclesIterable: {
            data: prefill.vehicles
        },
        ...getPrefillOrderInfoTableOverrides(),
        ...getPrefillReturnInfoTableOverrides(),
        ...getPrefillDriversTableOverrides(),
        ...getPrefillVehiclesOverrides()
    }

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />    
    )
};

MLRatingFactorPrefillComponent.propTypes = {
    jobOrPolicyNumber: PropTypes.string.isRequired,
    isQuote: PropTypes.bool.isRequired,
    isPolicy: PropTypes.bool.isRequired,
    isPersonalAuto: PropTypes.bool,
    isPersonalUmbrella: PropTypes.bool,
    styles: PropTypes.arrayOf(PropTypes.shape({}))
};

MLRatingFactorPrefillComponent.defaultProps = {
    isPersonalAuto: false,
    isPersonalUmbrella: false,
    styles: undefined
}

export default MLRatingFactorPrefillComponent;